// src/pages/Estatisticas/components/AccessByFunctionStats.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AccessByFunctionStats = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedEndpoints, setExpandedEndpoints] = useState(new Set());

  useEffect(() => {
    carregarEstatisticas();
  }, []);

  const carregarEstatisticas = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/estatisticas/acesso-por-funcao');
      
      if (response.data.success) {
        setStats(response.data.data);
        // Expandir automaticamente o primeiro endpoint
        if (response.data.data.length > 0) {
          setExpandedEndpoints(new Set([response.data.data[0].endpoint]));
        }
      } else {
        setError(response.data.message || 'Erro ao carregar estatísticas');
      }
    } catch (err) {
      console.error('Error loading access stats:', err);
      setError('Erro ao carregar estatísticas: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const toggleEndpoint = (endpoint) => {
    const newExpanded = new Set(expandedEndpoints);
    if (newExpanded.has(endpoint)) {
      newExpanded.delete(endpoint);
    } else {
      newExpanded.add(endpoint);
    }
    setExpandedEndpoints(newExpanded);
  };

  const formatarNumero = (numero) => {
    return numero.toLocaleString('pt-BR');
  };

  const formatarData = (dataString) => {
    const date = new Date(dataString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNomeFuncao = (endpoint) => {
    const mappings = {
      '/api/casos': 'Gestão de Casos',
      '/api/assistants': 'Assistentes de IA',
      '/api/chat': 'Chat com Casos',
      '/api/chatai': 'Chat com IA',
      '/api/documentos': 'Documentos',
      '/api/jurisprudencia': 'Jurisprudência',
      '/api/codigopenal': 'Código Penal',
      '/api/usuarios': 'Gerenciar Usuários',
      '/api/clients': 'Dados de Clientes',
      '/api/access-logs': 'Logs de Acesso',
      '/api/estatisticas': 'Estatísticas'
    };

    // Procurar por correspondência parcial
    for (const [key, value] of Object.entries(mappings)) {
      if (endpoint.startsWith(key)) {
        return value;
      }
    }

    return endpoint;
  };

  const getIconeFuncao = (endpoint) => {
    if (endpoint.includes('casos')) return '⚖️';
    if (endpoint.includes('chat')) return '💬';
    if (endpoint.includes('documentos')) return '📄';
    if (endpoint.includes('jurisprudencia')) return '🔎';
    if (endpoint.includes('codigopenal')) return '⚖️';
    if (endpoint.includes('usuarios')) return '👥';
    if (endpoint.includes('assistants')) return '🤖';
    if (endpoint.includes('client')) return '👤';
    if (endpoint.includes('estatisticas')) return '📊';
    return '🔧';
  };

  if (loading) {
    return (
      <div className="stat-box">
        <h3>👥 Acesso por Função e Usuário (Últimos 30 Dias)</h3>
        <div className="loading-stats">
          <span>⏳</span>
          <span>Carregando estatísticas de acesso...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stat-box">
        <h3>👥 Acesso por Função e Usuário (Últimos 30 Dias)</h3>
        <div className="error-stats">{error}</div>
      </div>
    );
  }

  if (stats.length === 0) {
    return (
      <div className="stat-box">
        <h3>👥 Acesso por Função e Usuário (Últimos 30 Dias)</h3>
        <div className="no-stats">
          <span>📊</span>
          <span>Nenhum acesso registrado no período</span>
        </div>
      </div>
    );
  }

  const totalAcessos = stats.reduce((sum, stat) => sum + stat.totalAccesses, 0);
  const totalUsuariosUnicos = new Set(
    stats.flatMap(stat => stat.users.map(u => u.email))
  ).size;

  return (
    <div className="stat-box">
      <h3>👥 Acesso por Função e Usuário (Últimos 30 Dias)</h3>
      
      <div className="access-summary-header">
        <div className="summary-item">
          <span className="summary-label">Total de Acessos:</span>
          <span className="summary-value">{formatarNumero(totalAcessos)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Funções Acessadas:</span>
          <span className="summary-value">{stats.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Usuários Ativos:</span>
          <span className="summary-value">{totalUsuariosUnicos}</span>
        </div>
      </div>

      <div className="access-functions-list">
        {stats.map((stat, index) => {
          const isExpanded = expandedEndpoints.has(stat.endpoint);
          const icone = getIconeFuncao(stat.endpoint);
          const nomeFuncao = getNomeFuncao(stat.endpoint);
          
          return (
            <div key={index} className="function-access-item">
              <div 
                className="function-header"
                onClick={() => toggleEndpoint(stat.endpoint)}
              >
                <div className="function-info">
                  <span className="function-icon">{icone}</span>
                  <div className="function-details">
                    <span className="function-name">{nomeFuncao}</span>
                    <span className="function-endpoint">{stat.endpoint}</span>
                  </div>
                </div>
                <div className="function-stats">
                  <span className="access-count">
                    {formatarNumero(stat.totalAccesses)} acessos
                  </span>
                  <span className="user-count">
                    {stat.users.length} usuário{stat.users.length !== 1 ? 's' : ''}
                  </span>
                  <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                    ▼
                  </span>
                </div>
              </div>

              {isExpanded && (
                <div className="users-table">
                  <div className="users-table-header">
                    <div className="user-cell">Usuário</div>
                    <div className="user-cell">Acessos</div>
                    <div className="user-cell">Último Acesso</div>
                  </div>
                  {stat.users.map((user, userIndex) => (
                    <div key={userIndex} className="users-table-row">
                      <div className="user-cell user-email">{user.email}</div>
                      <div className="user-cell access-count-cell">
                        {formatarNumero(user.accessCount)}
                      </div>
                      <div className="user-cell last-access-cell">
                        {formatarData(user.lastAccess)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="stats-summary">
        <p>Clique em uma função para ver detalhes dos usuários</p>
      </div>
    </div>
  );
};

export default AccessByFunctionStats;